import { RiCloseCircleFill } from '@remixicon/react';

import {
  Button,
  Flex,
  Icon,
  Text,
  VisuallyHidden,
} from '@spotify-internal/encore-web';
import { AiLoadingIndicator } from '@spotify/confidence-core-react';
import { useFeatureFlag } from '@spotify/confidence-flag-evaluation-react';

import {
  CONFIDENCE_CLOUD_LOADER_PHRASES,
  EXPERIMENTATION_LOADER_PHRASES,
} from './experimentationLoaderPhrases';
import { getSignupErrorMessage } from './signupUtils';

import styles from './ExperimentationLoader.module.css';

type ExperimentationLoaderProps = {
  error?: Error;
  onResultDismissed: () => void;
  onSignInAgain?: () => Promise<void>;
};

export const ExperimentationLoader = ({
  error,
  onResultDismissed,
  onSignInAgain,
}: ExperimentationLoaderProps) => {
  const { enabled: isConfidenceCloud } = useFeatureFlag<{
    enabled: boolean;
  }>('managed-warehouse', { enabled: false });
  const phrases = isConfidenceCloud
    ? CONFIDENCE_CLOUD_LOADER_PHRASES
    : EXPERIMENTATION_LOADER_PHRASES;

  if (!error) {
    return (
      <>
        <VisuallyHidden role="status">Creating workspace</VisuallyHidden>
        <div aria-hidden>
          <AiLoadingIndicator
            phrases={phrases}
            spinnerSize="large"
            textSize="medium"
            alignment="center"
          />
        </div>
      </>
    );
  }

  return (
    <Flex
      className={styles.result}
      direction="column"
      alignItems="center"
      gap="looser-2"
      role="alert"
    >
      <Icon
        as={RiCloseCircleFill}
        size="large"
        semanticColor="textNegative"
        aria-hidden
      />
      <Text variant="bodyMedium" semanticColor="textSubdued">
        {getSignupErrorMessage(error)}
      </Text>
      {onSignInAgain && (
        <Button variant="primary" size="medium" onClick={onSignInAgain} data-track-source-anchor="36221aafbd">
          Log in
        </Button>
      )}
      <Button
        variant="secondary-bordered"
        size="medium"
        onClick={onResultDismissed}
       data-track-source-anchor="92f808db2a">
        Back to workspace setup
      </Button>
    </Flex>
  );
};
